"use client";

import SearchableSelect from "./SearchableSelect";

export type FiltersState = {
  q: string;
  category: string;
  productType: string;
  brand: string;
  size: string;
  color: string;
  condition: string;
  city: string;
  min: string;
  max: string;
};

type Props = {
  value: FiltersState;
  onChange: (v: FiltersState) => void;
  options: {
    brands: string[];
    sizes: string[];
    colors: string[];
    cities: string[];
    types: string[];
  };
};

export default function Filters({ value, onChange, options }: Props) {
  const set = (k: keyof FiltersState, v: string) => onChange({ ...value, [k]: v });

  return (
    <div className="bg-[var(--card)] rounded-xl sm:rounded-2xl border border-[var(--border)] p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
        <div className="relative">
          <input
            className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--bg)] text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--text)]"
            placeholder="Pretraži odjeću ili brend..."
            value={value.q}
            onChange={(e) => set("q", e.target.value)}
          />
          {value.q && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition"
              onClick={() => set("q", "")}
              title="Očisti"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="relative">
          <select className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--bg)] text-xs sm:text-sm md:text-base text-[var(--text)] appearance-none pr-8" value={value.category} onChange={(e)=>set("category", e.target.value)}>
            <option value="">Sve kategorije</option>
            <option value="Žene">Žene</option>
            <option value="Muškarci">Muškarci</option>
            <option value="Djeca">Djeca</option>
          </select>
          {value.category && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition pointer-events-auto"
              onClick={() => set("category", "")}
              title="Očisti"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <SearchableSelect
          value={value.productType}
          onChange={(v) => set("productType", v)}
          options={options.types}
          placeholder="Svi tipovi"
          allowCustom={true}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        <SearchableSelect
          value={value.brand === "." ? "" : value.brand}
          onChange={(v) => set("brand", v === "." ? "" : v)}
          options={options.brands.filter((b) => b !== "." && b.trim() !== "")}
          placeholder="Sve marke"
          allowCustom={true}
        />

        <SearchableSelect
          value={value.size}
          onChange={(v) => set("size", v)}
          options={options.sizes}
          placeholder="Sve veličine"
          allowCustom={true}
        />

        <SearchableSelect
          value={value.color}
          onChange={(v) => set("color", v)}
          options={options.colors}
          placeholder="Sve boje"
          allowCustom={true}
        />

        <div className="relative">
          <select className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--bg)] text-xs sm:text-sm md:text-base text-[var(--text)] appearance-none pr-8" value={value.condition} onChange={(e)=>set("condition", e.target.value)}>
            <option value="">Sva stanja</option>
            <option value="Novo">Novo</option>
            <option value="Kao novo">Kao novo</option>
            <option value="Dobro">Dobro</option>
            <option value="Korektno">Korektno</option>
          </select>
          {value.condition && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition pointer-events-auto"
              onClick={() => set("condition", "")}
              title="Očisti"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        <SearchableSelect
          value={value.city}
          onChange={(v) => set("city", v)}
          options={options.cities}
          placeholder="Svi gradovi"
          allowCustom={true}
        />

        <div className="relative">
          <input className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--bg)] text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--text)]" placeholder="Min KM" value={value.min} onChange={(e)=>set("min", e.target.value)} />
          {value.min && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition"
              onClick={() => set("min", "")}
              title="Očisti"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="relative">
          <input className="w-full px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--bg)] text-xs sm:text-sm md:text-base text-[var(--text)] placeholder:text-[var(--text)]" placeholder="Max KM" value={value.max} onChange={(e)=>set("max", e.target.value)} />
          {value.max && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition"
              onClick={() => set("max", "")}
              title="Očisti"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <button
          className="px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--bg)] text-xs sm:text-sm md:text-base text-[var(--text)] font-bold hover:bg-[var(--bg)]/80 transition col-span-2 md:col-span-1"
          onClick={() =>
            onChange({ q:"", category:"", productType:"", brand:"", size:"", color:"", condition:"", city:"", min:"", max:"" })
          }
        >
          Reset filtera
        </button>
      </div>
    </div>
  );
}
